import React, { useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER } from '../../util/types';
import {
  Heading,
  H4,
  Reviews,
  ButtonTabNavHorizontal,
} from '../../components';

import css from './ProfilePage.module.css';

/**
 * Reviews about this user, split by the role they were reviewed in:
 * "Reviews from renters" (they hosted) and "Reviews from hosts" (they
 * rented). Both come from the ONE supported reviews query
 * (subject_id) — the old "Reviews given" tab was backed by an
 * `author_id` query the Marketplace API rejects with a 400, which is
 * why every profile showed "Loading reviews failed." (audit item 24).
 */

const ReviewsErrorMaybe = ({ queryReviewsError }) =>
  queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;

const MobileReviewsSection = props => {
  const { reviewsFromRenters, reviewsFromHosts, queryReviewsError } = props;

  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromRentersTitle"
          values={{ count: reviewsFromRenters.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      {reviewsFromRenters.length === 0 && !queryReviewsError ? (
        <p className={css.emptyReviews}>
          <FormattedMessage id="ProfilePage.noReviewsReceived" />
        </p>
      ) : (
        <Reviews reviews={reviewsFromRenters.slice(0, 10)} />
      )}

      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromHostsTitle"
          values={{ count: reviewsFromHosts.length }}
        />
      </H4>
      {reviewsFromHosts.length === 0 && !queryReviewsError ? (
        <p className={css.emptyReviews}>
          <FormattedMessage id="ProfilePage.noReviewsReceived" />
        </p>
      ) : (
        <Reviews reviews={reviewsFromHosts.slice(0, 10)} />
      )}
    </div>
  );
};

const DesktopReviewsSection = props => {
  const { reviewsFromRenters, reviewsFromHosts, queryReviewsError, intl } = props;

  const [showReviewsType, setShowReviewsType] = useState('fromRenters');
  const isFromRentersSelected = showReviewsType === 'fromRenters';

  const desktopReviewTabs = [
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromRentersTitle"
            values={{ count: reviewsFromRenters.length }}
          />
        </Heading>
      ),
      selected: isFromRentersSelected,
      onClick: () => setShowReviewsType('fromRenters'),
    },
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromHostsTitle"
            values={{ count: reviewsFromHosts.length }}
          />
        </Heading>
      ),
      selected: !isFromRentersSelected,
      onClick: () => setShowReviewsType('fromHosts'),
    },
  ];

  const shownReviews = isFromRentersSelected ? reviewsFromRenters : reviewsFromHosts;

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal
          className={css.desktopReviewsTabNav}
          tabs={desktopReviewTabs}
          ariaLabel={intl.formatMessage({ id: 'ProfilePage.screenreader.reviewsNav' })}
        />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {shownReviews.length === 0 && !queryReviewsError ? (
          <p className={css.emptyReviews}>
            <FormattedMessage id="ProfilePage.noReviewsReceived" />
          </p>
        ) : (
          <Reviews reviews={shownReviews.slice(0, 10)} />
        )}
      </div>
    </div>
  );
};

const ReviewSection = props => {
  const { reviews = [], queryReviewsError, intl, isMobileLayout } = props;

  const reviewsFromRenters = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsFromHosts = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

  return isMobileLayout ? (
    <MobileReviewsSection
      reviewsFromRenters={reviewsFromRenters}
      reviewsFromHosts={reviewsFromHosts}
      queryReviewsError={queryReviewsError}
    />
  ) : (
    <DesktopReviewsSection
      reviewsFromRenters={reviewsFromRenters}
      reviewsFromHosts={reviewsFromHosts}
      queryReviewsError={queryReviewsError}
      intl={intl}
    />
  );
};

export default ReviewSection;
